<div class="profile-settings layout-spacing">
    <div class="profile-settings__header">
        <h1 class="profile-settings__title">Profile Information</h1>
        <p class="profile-settings__subtitle">
            Manage your personal details and track your recent engagement.
        </p>
    </div>

    <div class="profile-settings__tabs">
        <ntv-tabs
            [tabs]="tabs.slice(0, 1)"
            [activeIndex]="activeTab()"
            (tabChange)="onTabChange($event)"></ntv-tabs>
    </div>

    @if (activeTab() === 0) {
        <form [formGroup]="profileForm" class="profile-settings__form">
            <ntv-card variant="elevated" rounded="xl" shadow="sm">
                <div class="content-card">
                    <div class="card-header flex-between">
                        <div class="card-header__left">
                            <div class="card-header__icon">
                                <svg width="24" height="24">
                                    <use [attr.href]="spritePath + '#' + icons.user"></use>
                                </svg>
                            </div>
                            <div class="card-header__info">
                                <h2 class="card-title">Edit Profile Info</h2>
                                <p class="card-subtitle">
                                    Modify basic user info and save changes.
                                </p>
                            </div>
                        </div>
                        @if (!isEditProfileEnabled()) {
                            <div class="profile-actions">
                                <ntv-button
                                    variant="accent"
                                    size="xl"
                                    (buttonClick)="onClickEditProfile()">
                                    Edit Profile
                                </ntv-button>
                                <div class="flex items-center gap-3">
                                    <ntv-button
                                        variant="accent"
                                        size="xl"
                                        [disabled]="changePasswordDisabled()"
                                        [attr.title]="changePasswordTooltip() || null"
                                        [attr.aria-disabled]="changePasswordDisabled()"
                                        (buttonClick)="onClickChangePassword()">
                                        Change Password
                                    </ntv-button>
                                    @if (changePasswordSecondsLeft() > 0) {
                                        <span class="profile-settings__cooldown-timer">
                                            {{ changePasswordCooldownDisplay() }}
                                        </span>
                                    }
                                </div>
                            </div>
                        } @else {
                            <div class="profile-actions">
                                <ntv-button
                                    variant="outline"
                                    size="xl"
                                    (buttonClick)="onClickCancelEditProfile()">
                                    Cancel
                                </ntv-button>
                                <ntv-button
                                    variant="accent"
                                    size="xl"
                                    [disabled]="!canSaveProfile()"
                                    (buttonClick)="onSaveProfile()">
                                    Save Profile
                                </ntv-button>
                            </div>
                        }
                    </div>

                    <div
                        class="user-form-grid"
                        [class.user-form-grid--readonly]="!isEditProfileEnabled()">
                        <div class="profile-field">
                            <ntv-input
                                label="User Type"
                                formControlName="userType"
                                placeholder="User Type" />
                        </div>
                        <div class="profile-field">
                            <ntv-input
                                label="Email Address"
                                formControlName="emailAddress"
                                placeholder="Email Address" />
                        </div>
                        <div class="profile-field">
                            <ntv-input
                                label="First Name"
                                formControlName="firstName"
                                [maxLength]="profileFieldMaxLength"
                                [info]="fieldLengths().firstName + '/' + profileFieldMaxLength"
                                placeholder="First Name" />
                            @if (isProfileFieldInvalid('firstName')) {
                                <p class="profile-field__error">
                                    {{ getProfileFieldErrorMessage('firstName') }}
                                </p>
                            }
                        </div>
                        <div class="profile-field">
                            <ntv-input
                                label="Last Name"
                                formControlName="lastName"
                                [maxLength]="profileFieldMaxLength"
                                [info]="fieldLengths().lastName + '/' + profileFieldMaxLength"
                                placeholder="Last Name" />
                            @if (isProfileFieldInvalid('lastName')) {
                                <p class="profile-field__error">
                                    {{ getProfileFieldErrorMessage('lastName') }}
                                </p>
                            }
                        </div>
                        <div class="profile-field">
                            <ntv-input
                                label="Contact Number"
                                formControlName="contactNumber"
                                inputmode="numeric"
                                pattern="[0-9]*"
                                [maxLength]="contactNumberMaxLength"
                                [info]="fieldLengths().contactNumber + '/' + contactNumberMaxLength"
                                (keydown)="onContactNumberKeydown($event)"
                                (input)="onContactNumberInput($event)"
                                placeholder="Contact Number" />
                            @if (isProfileFieldInvalid('contactNumber')) {
                                <p class="profile-field__error">
                                    {{ getProfileFieldErrorMessage('contactNumber') }}
                                </p>
                            }
                        </div>
                    </div>
                </div>
            </ntv-card>
        </form>
    }

    <!-- Discard Unsaved Changes Warning Modal -->
    @if (showCancelEditConfirmModal()) {
        <app-confirmation-modal
            [isVisible]="showCancelEditConfirmModal()"
            type="warning"
            title="Unsaved Changes"
            message="You have unsaved changes. Are you sure you want to discard them?"
            confirmText="Discard"
            cancelText="Keep Editing"
            [showCancelButton]="true"
            (confirm)="onConfirmDiscardChanges()"
            (cancel)="onDismissDiscardChanges()"
            (modalClose)="onDismissDiscardChanges()" />
    }

    <!-- Profile Save Success Modal -->
    @if (showSaveProfileModal()) {
        <app-confirmation-modal
            [isVisible]="showSaveProfileModal()"
            type="success"
            title="Profile Updated"
            message="Your profile information has been updated successfully."
            confirmText="Got it"
            [showCancelButton]="false"
            (confirm)="onCloseSaveProfileModal()"
            (modalClose)="onCloseSaveProfileModal()" />
    }

    <!-- Change Password Confirmation Modal -->
    @if (showChangePasswordModal()) {
        <app-confirmation-modal
            [isVisible]="showChangePasswordModal()"
            type="success"
            title="Check Your Email"
            [message]="changePasswordMessage()"
            confirmText="Got it"
            [showCancelButton]="false"
            (confirm)="onCloseChangePasswordModal()"
            (modalClose)="onCloseChangePasswordModal()" />
    }
</div>
